// 분석 보고서 작성 화면이 다루는 내부 상태 -> 서버로 보내는 "선택 JSON" 변환.
// 키 이름은 영구히 aggregations다(특정 국내 등록특허와 모양이 닮지 않도록 - 근거는
// backend/docs/분석보고서_설계결정기록.md R9 보강). 이 파일 자체에도 그 두 영문 용어를
// (설명을 위해서라도) 쓰지 않는다.
//
// 서버가 검증을 다시 하므로(모든 실행/저장이 §4-2를 통과해야 한다) 여기는 화면 조작을 위한
// 가벼운 변환/도우미만 담는다 - 식별자 화이트리스트·주입 방지 같은 보안 로직은 없다(서버 책임).

/** 빈 값/빈 배열을 정리해 서버가 받는 모양으로 바꾼다. */
export function buildSelection({ mode, columns, groupBy, aggregations, filters, sort, limit }) {
  const sel = { mode };
  if (mode === 'detail') {
    sel.columns = (columns || []).filter(Boolean);
  } else {
    sel.groupBy = (groupBy || []).map((g) => (g.bucket ? { field: g.field, bucket: g.bucket } : { field: g.field }));
    sel.aggregations = (aggregations || []).map((a) => ({ field: a.field, func: a.func }));
  }
  const cleanFilters = (filters || [])
    .filter((f) => f.field && f.op)
    .map((f) => {
      const out = { field: f.field, op: f.op };
      if (f.op === 'IN') {
        out.values = f.values || [];
      } else if (f.op === 'BETWEEN') {
        out.value1 = f.value1;
        out.value2 = f.value2;
      } else if (f.op === 'RELATIVE') {
        // 7번(Fable 재검토 2026-10-26): 상대 기간 - value=기간 코드(THIS_MONTH 등),
        // LAST_N_DAYS일 때만 value1=N. 절대 날짜 변환은 서버가 실행 시점에 한다.
        out.value = f.value;
        if (f.value === 'LAST_N_DAYS') {
          out.value1 = f.value1;
        }
      } else {
        out.value = f.value;
      }
      return out;
    });
  if (cleanFilters.length) {
    sel.filters = cleanFilters;
  }
  if (sort && sort.length) {
    sel.sort = sort;
  }
  if (limit) {
    sel.limit = limit;
  }
  return sel;
}

/** 저장된 선택 JSON(서버 모양)을 작성 화면 내부 상태로 되돌린다(편집/복사 진입용). */
export function parseSelection(selJson) {
  const sel = typeof selJson === 'string' ? JSON.parse(selJson) : selJson || {};
  return {
    mode: sel.mode === 'summary' ? 'summary' : 'detail',
    columns: sel.columns || [],
    groupBy: sel.groupBy || [],
    aggregations: sel.aggregations || [],
    filters: (sel.filters || []).map((f) => ({
      field: f.field,
      op: f.op,
      value: f.value,
      value1: f.value1,
      value2: f.value2,
      values: f.values,
    })),
    sort: sel.sort || [],
    limit: sel.limit || 100,
  };
}

/** 요약형에서 그룹/집계가 쓸 결과 별칭을 미리 붙여 화면에 보여줄 열 순서를 만든다(서버와 같은 규칙). */
export function previewAliases(groupBy, aggregations) {
  const g = (groupBy || []).map((_, i) => `g${i}`);
  const m = (aggregations || []).map((_, i) => `m${i}`);
  return [...g, ...m];
}

/**
 * 서버 오류 메시지는 "RPT_FIELD_NOT_ALLOWED: 쓸 수 없는 필드입니다: PRJ_NO"처럼 앞에 내부 코드가
 * 붙어 온다(GlobalExceptionHandler가 IllegalArgumentException.getMessage()를 그대로 내려줌).
 * 코드 자체는 로그/버그 리포트용이고, 화면에는 콜론 뒤 사람이 읽는 문장만 보여준다.
 */
export function friendlyErrorMessage(err, fallback) {
  const raw = err?.response?.data?.message;
  if (!raw) return fallback;
  const match = /^RPT_[A-Z0-9_]+:\s*(.+)$/.exec(raw);
  return match ? match[1] : raw;
}

export const VIEW_TYPES = ['TABLE', 'BAR', 'LINE'];
export const SHARE_TYPES = ['PRIVATE', 'COMPANY'];
export const BUCKETS = [
  { value: 'day', label: '일' },
  { value: 'week', label: '주' },
  { value: 'month', label: '월' },
  { value: 'quarter', label: '분기' },
  { value: 'year', label: '연' },
];

// 6번(Fable 재검토 2026-10-26): 조건 칩/드롭다운이 FilterBuilder와 ReportViewContainer 양쪽에서
// 각자 다른 OP_LABEL을 쓰고 있었다 - 한 곳으로 모은다.
export const OP_LABEL = {
  EQ: '=', NE: '≠', IN: '목록 포함', BETWEEN: '구간', GTE: '≥', LTE: '≤', LIKE: '포함(검색어)', RELATIVE: '상대 기간',
};

// 7번: 상대 기간 선택지(서버 RptRelativePeriod.CODES와 1:1로 맞춘다).
export const RELATIVE_OPTIONS = [
  { value: 'TODAY', label: '오늘' },
  { value: 'THIS_WEEK', label: '이번 주' },
  { value: 'THIS_MONTH', label: '이번 달' },
  { value: 'LAST_MONTH', label: '지난 달' },
  { value: 'THIS_QUARTER', label: '이번 분기' },
  { value: 'THIS_YEAR', label: '올해' },
  { value: 'LAST_N_DAYS', label: '최근 N일' },
];
export const RELATIVE_LABEL = Object.fromEntries(RELATIVE_OPTIONS.map((o) => [o.value, o.label]));

/** 조건 1개를 사람이 읽는 문장으로 - 필드 코드 대신 표시명을 쓴다(6번, "조건 칩이 필드 코드로
 * 보인다"). fieldsByCode가 없거나 그 필드를 못 찾으면 코드를 그대로 보여준다(깨지지 않게). */
export function describeFilter(f, fieldsByCode) {
  const label = fieldsByCode?.[f.field]?.fieldNm || f.field;
  const opLabel = OP_LABEL[f.op] || f.op;
  let valueText;
  if (f.op === 'IN') {
    valueText = (f.values || []).join('/');
  } else if (f.op === 'BETWEEN') {
    valueText = `${f.value1 || ''}~${f.value2 || ''}`;
  } else if (f.op === 'RELATIVE') {
    valueText = f.value === 'LAST_N_DAYS' ? `최근 ${f.value1 || ''}일` : (RELATIVE_LABEL[f.value] || f.value);
  } else {
    const opt = fieldsByCode?.[f.field]?.codeOptions?.find((o) => o.value === f.value);
    valueText = opt ? opt.label : f.value;
  }
  return `${label} ${opLabel} ${valueText}`;
}
